import React from 'react';
import dayjs from 'dayjs';

import NoticeBannerList from './NoticeBannerList';
import BannerMessageEntity from 'Models/Entities/BannerMessageEntity';
import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';

interface BookingWizardNoticeBannerProps {
	selectedTrips: SelectedTrips;
	/** The locations the customer is browsing between, known before any sailing is picked. */
	fromLocationId?: string;
	toLocationId?: string;
	/** True when the wizard is showing the return leg as well, so the reverse route is on screen too. */
	includeReturnRoute?: boolean;
	/** The window of sailings currently listed, used while no sailing has been picked. */
	browsingStartDate?: Date;
	browsingEndDate?: Date;
}

/**
 * The notice banners for the booking wizard. Unlike the schedule page — where the customer is browsing
 * a week of sailings — the wizard knows exactly which trips they are booking, so both the routes and
 * the travel dates come from those trips.
 *
 * Until a sailing is picked there are no trips to read, so it falls back to the route the customer is
 * looking at and the window of sailings on screen. A notice about a route matters most while they are
 * still choosing — waiting for a selection would hide it for exactly as long as it could change their
 * mind.
 *
 * Only banners staff have opted into the wizard appear here; the rest stay on the schedule page.
 */
export default function BookingWizardNoticeBanner({
	selectedTrips,
	fromLocationId,
	toLocationId,
	includeReturnRoute = false,
	browsingStartDate,
	browsingEndDate,
}: BookingWizardNoticeBannerProps) {
	const journeyElements = journeyElementsStore();

	const trips = [selectedTrips.departingTrip, selectedTrips.returningTrip].filter(trip => !!trip);

	const selectedRouteIds = Array.from(
		new Set(trips.map(trip => trip?.routeId).filter((routeId): routeId is string => !!routeId)),
	);

	// The route(s) on screen: the outbound leg, plus its reverse when a return is being booked.
	const browsingRouteIds =
		fromLocationId && toLocationId
			? journeyElements.routes
					.filter(
						route =>
							(route.departureId === fromLocationId && route.destinationId === toLocationId) ||
							(includeReturnRoute &&
								route.departureId === toLocationId &&
								route.destinationId === fromLocationId),
					)
					.map(route => route.id)
			: [];

	// Union, not a fallback: picking a sailing adds certainty, it doesn't remove the legs still on
	// screen. Swapping the browsing routes out on selection meant a notice could vanish the moment the
	// customer chose a trip — the step still lists both legs, and a trip's own route id doesn't always
	// match the route resolved from the two locations.
	const routeIds = Array.from(new Set([...selectedRouteIds, ...browsingRouteIds]));

	const departureTimes = trips
		.map(trip => trip?.departureDateTime)
		.filter((departure): departure is Date => !!departure)
		.map(departure => dayjs(departure));

	/**
	 * On a return booking a notice often applies to only one leg, and "your booking is affected" isn't
	 * much help when the customer is holding two sailings. Naming the leg by its locations — rather than
	 * the route's own name, which staff write for staff — says which half is affected.
	 *
	 * Returns nothing when there is only one leg, or when the banner covers both: there is no ambiguity
	 * to resolve, and a label on every banner would just be noise.
	 */
	const scopeLabelFor = (banner: BannerMessageEntity): string | undefined => {
		if (routeIds.length < 2) {
			return undefined;
		}

		const affected = routeIds.filter(routeId => (banner.routeIds ?? []).includes(routeId));
		if (affected.length !== 1) {
			return undefined;
		}

		const route = journeyElements.routes.find(r => r.id === affected[0]);
		const from = route?.departure?.name;
		const to = route?.destination?.name;

		// Without both location names there is nothing clearer to say than the banner already says.
		return from && to ? `Applies to ${from} → ${to}` : undefined;
	};

	// A return booking spans both legs, so the range runs from the earlier departure to the later one.
	// A MatchTripDates banner then has to overlap the customer's actual travel, not a browsing window —
	// so unlike the routes above, a selection replaces the window here rather than widening it, or a
	// date-matched notice could show against travel it doesn't cover. With nothing picked yet there is
	// no travel to overlap, so the window of sailings on screen stands in.
	const tripRange =
		departureTimes.length > 0
			? {
					start: dayjs(Math.min(...departureTimes.map(time => time.valueOf())))
						.startOf('day')
						.toDate(),
					end: dayjs(Math.max(...departureTimes.map(time => time.valueOf())))
						.endOf('day')
						.toDate(),
				}
			: browsingStartDate && browsingEndDate
				? {
						start: dayjs(browsingStartDate).startOf('day').toDate(),
						end: dayjs(browsingEndDate).endOf('day').toDate(),
					}
				: undefined;

	// A route is the minimum a banner can be matched on. Without a date range only the date-independent
	// banners can be judged, so the list still renders — NoticeBannerList decides the rest.
	if (routeIds.length === 0) {
		return <></>;
	}

	return (
		<NoticeBannerList
			surface="bookingWizard"
			routeIds={routeIds}
			tripRange={tripRange}
			scopeLabelFor={scopeLabelFor}
			className="booking-wizard-notice-banner"
		/>
	);
}
